import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { Reveal } from "./Motion";
import { signupUrl } from "@/data/networks";
import { track } from "@/lib/track";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const FeaturedVideo = ({ n }) => {
  const [start, setStart] = useState(null); // null = not playing; number = start second
  const playerRef = useRef(null);
  const v = n.video;
  const playing = start !== null;
  const embed = `https://www.youtube-nocookie.com/embed/${v.youtubeId}?autoplay=1&rel=0&modestbranding=1&start=${start ?? 0}`;

  const play = (at = 0, chapter = "") => {
    if (!playing) track("video_play", n.key, { chapter });
    setStart(at);
  };
  const jump = (c) => {
    play(c.at, c.label);
    playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section id="video" className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-24 sm:pb-32 scroll-mt-24" data-testid="featured-video-section">
      <Reveal className="grid lg:grid-cols-[1fr_2fr] gap-6 lg:gap-12 items-end mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: n.accent }}>Featured video</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1F1A17]" data-testid="featured-video-title">
            {v.title}
          </h2>
        </div>
        <p className="text-base leading-relaxed text-[#685F58] lg:max-w-xl lg:justify-self-end">{v.caption}</p>
      </Reveal>

      <Reveal y={40}>
        <div className="relative" ref={playerRef}>
          <div className="absolute -inset-3 sm:-inset-5 rounded-[2.5rem] -rotate-1" style={{ background: `${n.accent}12` }} />
          <div className="relative aspect-video overflow-hidden rounded-[2rem] border border-[#E8DDD3] bg-[#181412] shadow-[0_40px_80px_-30px_rgba(31,26,23,0.45)]">
            {playing ? (
              <iframe
                key={start}
                data-testid="featured-video-iframe"
                className="absolute inset-0 h-full w-full"
                src={embed}
                title={v.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <button
                type="button"
                onClick={() => play(0)}
                data-testid="featured-video-play-btn"
                aria-label={`Play video: ${v.title}`}
                className="group absolute inset-0 h-full w-full text-left"
              >
                <img src={v.poster} alt={v.posterAlt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#181412]/80 via-[#181412]/20 to-transparent" />
                <div className="absolute inset-0 hv-spotlight" />

                <span className="absolute inset-0 grid place-items-center">
                  <motion.span
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.96 }}
                    className="relative grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded-full text-white shadow-2xl"
                    style={{ background: n.accent }}
                  >
                    <span className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: n.accent }} />
                    <Play className="relative h-8 w-8 sm:h-9 sm:w-9 translate-x-0.5" fill="currentColor" />
                  </motion.span>
                </span>

                <span className="absolute left-5 right-5 bottom-5 sm:left-8 sm:right-8 sm:bottom-8 flex flex-wrap items-end justify-between gap-4">
                  <span className="rounded-2xl bg-white/90 backdrop-blur-md border border-[#E8DDD3] px-4 py-3 shadow-xl">
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">Now showing</span>
                    <span className="block text-sm font-medium text-[#1F1A17]">{v.title}</span>
                  </span>
                  <span className="rounded-full bg-[#181412]/70 backdrop-blur-md px-3 py-1.5 font-mono text-xs text-[#FBF8F4]" data-testid="featured-video-duration">
                    {v.duration}
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-8" delay={0.1}>
        <ol className="grid sm:grid-cols-3 gap-3" data-testid="video-chapters" aria-label="Video chapters">
          {v.chapters.map((c, i) => {
            const active = playing && start === c.at;
            return (
              <li key={c.label}>
                <button
                  type="button"
                  onClick={() => jump(c)}
                  data-testid={`video-chapter-${i}`}
                  aria-current={active ? "true" : undefined}
                  className={`group w-full text-left rounded-2xl border p-5 transition-colors duration-300 ${
                    active ? "border-[#1F1A17] bg-[#1F1A17] text-[#FBF8F4]" : "border-[#E8DDD3] bg-white hover:border-[#1F1A17]"
                  }`}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${active ? "text-[#D4A373]" : "text-[#685F58]"}`}>Chapter 0{i + 1}</span>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-xs ${active ? "bg-[#FBF8F4]/10 text-[#FBF8F4]" : "text-white"}`} style={active ? undefined : { background: n.accent }}>
                      <Play className="h-3 w-3" fill="currentColor" /> {fmt(c.at)}
                    </span>
                  </span>
                  <span className={`mt-3 block font-serif text-2xl tracking-tight transition-transform duration-500 group-hover:translate-x-1 ${active ? "text-[#FBF8F4]" : "text-[#1F1A17]"}`}>{c.label}</span>
                  <span className={`mt-1.5 block text-sm leading-relaxed ${active ? "text-[#C8BFB8]" : "text-[#685F58]"}`}>{c.d}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </Reveal>

      <Reveal className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
        <a href={signupUrl(n.key)} data-testid="featured-video-signup-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] h-12 px-6 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
          Seen enough? Create your vault — free
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
        <span className="text-sm text-[#685F58]">Takes about two minutes. No card needed.</span>
      </Reveal>
    </section>
  );
};
